var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { rechartsDiagonalStripeSwatchCss } from '@web/components/layout/RechartsItems/rechartsDiagonalStripes';
import { SHARED_FILES_CONFIG } from '@web/config';
import { classNames } from '@web/utility';
import { ComparisonPeriod, PeriodComparisonSectionHeader, resolvePeriodComparisonSectionLabels } from '@web/utility/ tooltip/periodComparisonTooltip';
import { TOOLTIP_PANEL_RECHARTS_CLASS, TOOLTIP_SERIES_SEPARATOR_CLASS } from '@web/utility/ tooltip/tremorTooltip';
import { useTranslation } from 'react-i18next';
var previousSeriesOrder = [
    'previousInternalSharing',
    'previousExternalSharing'
];
var userSeriesOrder = ['userInternalSharing', 'userExternalSharing'];
var teamSeriesOrder = ['teamInternalSharing', 'teamExternalSharing'];
function chartColorForDataKey(dataKey, userId) {
    if (dataKey === 'previousInternalSharing') {
        return SHARED_FILES_CONFIG.internalSharing.prevChartColor;
    }
    if (dataKey === 'previousExternalSharing') {
        return SHARED_FILES_CONFIG.externalSharing.prevChartColor;
    }
    if (dataKey === 'userInternalSharing') {
        return SHARED_FILES_CONFIG.internalSharing.chartColor;
    }
    if (dataKey === 'userExternalSharing') {
        return SHARED_FILES_CONFIG.externalSharing.chartColor;
    }
    if (dataKey === 'teamInternalSharing') {
        return userId
            ? SHARED_FILES_CONFIG.internalSharingTeam.chartColor
            : SHARED_FILES_CONFIG.internalSharing.chartColor;
    }
    if (dataKey === 'teamExternalSharing') {
        return userId
            ? SHARED_FILES_CONFIG.externalSharingTeam.chartColor
            : SHARED_FILES_CONFIG.externalSharing.chartColor;
    }
    return '#6b7280';
}
function metricLabelForDataKey(dataKey, t) {
    if (dataKey.includes('Internal')) {
        return t('tooltip.metrics.internalSharing');
    }
    if (dataKey.includes('External')) {
        return t('tooltip.metrics.externalSharing');
    }
    return dataKey;
}
export function SharedFilesBarChartTooltip(_a) {
    var _b, _c;
    var active = _a.active, payload = _a.payload, label = _a.label, userId = _a.userId, dateFrom = _a.dateFrom, dateTo = _a.dateTo;
    var t = useTranslation().t;
    if (!active || !payload || !payload.length)
        return null;
    var basePayload = userId
        ? payload
        : payload.filter(function (entry) {
            return typeof entry.dataKey === 'string' &&
                (entry.dataKey.includes('team') || entry.dataKey.includes('previous'));
        });
    var payloadByKey = new Map(basePayload
        .filter(function (e) { return typeof e.dataKey === 'string'; })
        .map(function (e) {
        var _a, _b;
        return [
            String(e.dataKey),
            {
                value: Number((_a = e.value) !== null && _a !== void 0 ? _a : 0),
                color: String((_b = e.color) !== null && _b !== void 0 ? _b : '')
            }
        ];
    }));
    var keysForTooltip = userId
        ? __spreadArray(__spreadArray(__spreadArray([], previousSeriesOrder, true), userSeriesOrder, true), teamSeriesOrder, true) : __spreadArray(__spreadArray([], previousSeriesOrder, true), teamSeriesOrder, true);
    var resolvedRows = keysForTooltip.map(function (dataKey) {
        var _a;
        var fromPayload = payloadByKey.get(dataKey);
        var value = (_a = fromPayload === null || fromPayload === void 0 ? void 0 : fromPayload.value) !== null && _a !== void 0 ? _a : 0;
        var color = (fromPayload === null || fromPayload === void 0 ? void 0 : fromPayload.color) || chartColorForDataKey(dataKey, userId);
        return { dataKey: dataKey, value: value, color: color };
    });
    if (resolvedRows.every(function (r) { return r.value === 0; }))
        return null;
    var previousEntries = resolvedRows.filter(function (r) {
        return previousSeriesOrder.includes(r.dataKey);
    });
    var userEntries = userId
        ? resolvedRows.filter(function (r) {
            return userSeriesOrder.includes(r.dataKey);
        })
        : [];
    var teamEntries = resolvedRows.filter(function (r) {
        return teamSeriesOrder.includes(r.dataKey);
    });
    var periodPayload = ((_c = (_b = payload[0]) === null || _b === void 0 ? void 0 : _b.payload) !== null && _c !== void 0 ? _c : {});
    var _d = resolvePeriodComparisonSectionLabels(String(label !== null && label !== void 0 ? label : ''), periodPayload, dateFrom, dateTo), currentPeriodLabel = _d.current, previousPeriodLabel = _d.previous;
    var row = function (entry, index) {
        var isStripedTeamRow = Boolean(userId) &&
            (entry.dataKey === 'teamInternalSharing' ||
                entry.dataKey === 'teamExternalSharing');
        var teamStripeColor = chartColorForDataKey(String(entry.dataKey), userId);
        return (_jsxs("div", __assign({ className: "flex items-center justify-between gap-5" }, { children: [_jsxs("div", __assign({ className: "flex items-center gap-2" }, { children: [isStripedTeamRow ? (_jsx("div", { className: "h-2 w-2 shrink-0 rounded-full border border-gray-200", style: {
                                background: rechartsDiagonalStripeSwatchCss(teamStripeColor)
                            }, "aria-hidden": true })) : (_jsx("div", { className: "h-2 w-2 rounded-full", style: { backgroundColor: entry.color } })), _jsx("span", __assign({ className: "text-sm text-gray-600" }, { children: metricLabelForDataKey(String(entry.dataKey), t) }))] })), _jsx("span", __assign({ className: "text-sm font-semibold text-gray-900" }, { children: entry.value }))] }), "".concat(String(entry.dataKey), "-").concat(index)));
    };
    var sections = [];
    if (previousEntries.length > 0) {
        sections.push({
            key: ComparisonPeriod.Previous,
            entries: previousEntries,
            periodLabel: previousPeriodLabel
        });
    }
    if (userId && userEntries.length > 0) {
        sections.push({
            key: ComparisonPeriod.Current,
            entries: userEntries,
            periodLabel: currentPeriodLabel
        });
    }
    if (teamEntries.length > 0) {
        sections.push({
            key: userId ? ComparisonPeriod.Team : ComparisonPeriod.Current,
            entries: teamEntries,
            periodLabel: userId ? undefined : currentPeriodLabel
        });
    }
    return (_jsx("div", __assign({ className: classNames(TOOLTIP_PANEL_RECHARTS_CLASS, 'min-w-[200px]') }, { children: _jsx("div", __assign({ className: "flex flex-col gap-3" }, { children: sections.map(function (section, sectionIndex) { return (_jsxs("div", { children: [sectionIndex > 0 && (_jsx("div", { className: TOOLTIP_SERIES_SEPARATOR_CLASS, role: "separator" })), _jsx(PeriodComparisonSectionHeader, { section: section.key, periodLabel: section.periodLabel, t: t }), _jsx("div", __assign({ className: "mt-1.5 flex flex-col gap-2" }, { children: section.entries.map(function (entry, index) { return row(entry, index); }) }))] }, section.key)); }) })) })));
}
